import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import takeWhile from 'lodash/takeWhile';
import { format, startOfDay } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ConsolidatedForecastDetail,
  ConsolidatedForecastListItem
} from '@futrli-sdk/consolidations';
import {
  Logger,
  PREDICT_PRODUCT_SLUG,
  getConsolidatedForecastingUrl,
  usePromiseState,
  ROUTES
} from '@futrli/shared-services';
import { AiAssumption, FinancialConfig, Forecast } from '@futrli-sdk/predict';
import { toastError } from '@futrli/ui-components';
import { clearConsolidatedForecastsQueryCache } from '@futrli/views/queries/consolidatedForecasts';

import {
  readConsolidatedForecast,
  updateConsolidatedForecast,
  deleteConsolidatedForecast,
  readConsolidatedForecastReport,
  bulkUpdateConsolidatedForecast
} from '../../../core/api/consolidated-forecasting';
import { ConsolidatedForecastUpdateData } from '../../../core/api/types/consolidated-forecasting';
import { useConsolidationDataContext } from '../../../predict-management/context/consolidation-data-context';
import { ConsolidatedForecastReport, QueryParams } from '../../../core/types';
import { detailSettingsPersistor } from './detail-settings-persistor';
import { PERIOD_TYPE_REQUEST_DATA, PeriodType } from '../../../core/constants';
import { forceLocalDate } from '../../../utils/dates';
import { isPastCell } from '../../../ui/data-grid/components/current-period/current-period';
import { getColumnsWithFYInfo } from '../predictions/utils/forecast-columns';
import { useConsolidatedForecastingContext } from './consolidated-forecasting-context';
import { deleteConsolidatedAssumption, patchConsolidatedAssumption } from '../../../core/api/consolidated-assumptions';
import { AllowedAssumptionTypes } from '../reports/actions/actions';
import { BulkUpdatedPredictionsPayload } from '../../../ui/assistant-side-panel/bulk-update/types';
import { useTotalCashPredictRow } from '../predictions/hooks/useTotalCashPredictRow/useTotalCashPredictRow';
import { TotalRow } from '../reports/data-grid/types';

interface ConsolidatedForecastContextProps {
  children?: ReactNode;
  forecastId: string;
}

interface ConsolidatedForecastContextValues {
  deleteForecast: () => Promise<void>;
  fetchForecast: () => Promise<void>;
  fetchForecastReport: (_page?: string, periodTypeParam?: PeriodType) => Promise<void>;
  updateForecast: (data: ConsolidatedForecastUpdateData | Partial<Forecast>) => Promise<void>;
  forecast?: ConsolidatedForecastDetail;
  forecastId: string;
  isLoading: boolean;
  isFetchingReport: boolean;
  totalPastDates?: number;
  forecastGraphDate?: string;
  periodType: PeriodType;
  refresh: () => void;
  setPeriodType: (periodType: PeriodType) => void;
  consolidatedForecastReport: ConsolidatedForecastReport | null;
  totalCashPredictRow: TotalRow | null;
}

const context = createContext(({} as unknown) as ConsolidatedForecastContextValues);

export const ConsolidatedForecastContext = ({ children, forecastId }: ConsolidatedForecastContextProps) => {
  const { workspaceId, consolidationId } = useConsolidationDataContext();
  const { loadForecasts } = useConsolidatedForecastingContext();
  const [deleteForecastReq, setDeleteForecastReq] = usePromiseState<void>();
  const [forecastReq, setForecastReq] = usePromiseState<ConsolidatedForecastDetail>();
  const [updateForecastReq, setUpdateForecastReq] = usePromiseState<ConsolidatedForecastListItem>();
  const [updateAssumptionReq, setUpdateAssumptionReq] = usePromiseState<void>();
  const [deleteAssumptionReq, setDeleteAssumptionReq] = usePromiseState<void>();
  const [bulkUpdateAssumptionReq, setBulkUpdateAssumptionReq] = usePromiseState<void>();
  const [consolidatedForecastReportInfo, setConsolidatedForecastReportInfo] = useState<{
    forecastReport?: ConsolidatedForecastReport;
    periodType: PeriodType;
  }>({ forecastReport: undefined, periodType: detailSettingsPersistor.instance.periodType });
  const [isLoadingForecastReport, setIsLoadingForecastReport] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { consolidation } = useConsolidationDataContext();

  const { totalCashPredictRow } = useTotalCashPredictRow(
    workspaceId,
    { id: consolidationId, isConsolidation: true },
    forecastReq.data,
    consolidatedForecastReportInfo.forecastReport,
    location.pathname.includes(ROUTES.INVOICES)
  );

  const getForecastGraphDate = useCallback((forecast_from_date?: string) => {
    if (!forecast_from_date) {
      return;
    }

    return format(startOfDay(forceLocalDate(forecast_from_date)), 'yyyy-MM-dd');
  }, []);

  const loadForecastReport = useCallback(
    async (_page?: string, periodTypeParam?: PeriodType, quiet?: boolean) => {
      try {
        setIsLoadingForecastReport(!quiet);
        const periodTypeToUse = periodTypeParam || consolidatedForecastReportInfo.periodType;

        const newForecastReport = await readConsolidatedForecastReport(
          workspaceId,
          consolidationId,
          forecastId,
          PERIOD_TYPE_REQUEST_DATA[periodTypeToUse] as QueryParams
        );
        setConsolidatedForecastReportInfo({
          forecastReport: newForecastReport,
          periodType: periodTypeToUse
        });
      } catch (e) {
        toastError();
        Logger.error(e as string);
      } finally {
        setIsLoadingForecastReport(false);
      }
    },
    [consolidatedForecastReportInfo.periodType, workspaceId, consolidationId, forecastId]
  );

  const setPeriodType = useCallback(
    async (newPeriodType: PeriodType) => {
      await loadForecastReport(undefined, newPeriodType);
      detailSettingsPersistor.persist({ periodType: newPeriodType });
    },
    [loadForecastReport]
  );

  const getTotalPastDates = useCallback(
    (currentPeriodType: PeriodType, columns?: string[], forecast?: ConsolidatedForecastListItem) => {
      if (!consolidation || !columns || !forecast) {
        return;
      }
      const { financial_year_end_day, financial_year_end_month } = consolidation;
      const financialConfig: FinancialConfig = {
        year_end: {
          day: financial_year_end_day,
          month: financial_year_end_month
        },
        current_year: {
          start: '',
          end: ''
        },
        reporting_lineref_version: 2,
        accounting_rules: 'default',
        allow_google_sheet_import: true,
        allow_reorganising_groups: true,
        unreconciled_amount: null,
        unreconciled_transactions_count: null,
        dimensions: [],
        dimensions_enabled: false
      };
      const pastColumns = takeWhile(columns, (item: string) =>
        isPastCell(item, currentPeriodType, forecast.forecast_from_date)
      );
      if (financialConfig) {
        const pastColumnsWithFYInfo = getColumnsWithFYInfo(pastColumns, financialConfig);
        return pastColumnsWithFYInfo.length;
      }
      return pastColumns.length;
    },
    [consolidation]
  );

  const loadForecast = useCallback(
    async (quiet = false) => {
      try {
        await setForecastReq(readConsolidatedForecast(workspaceId, consolidationId, forecastId), quiet);
      } catch (e) {
        Logger.error(e as string);
      }
    },
    [consolidationId, forecastId, setForecastReq, workspaceId]
  );

  const updateAssumption = useCallback(
    async (
      data: Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>,
      workspaceId: string,
      consolidationId: string,
      forecastId: string,
      assumptionId: string
    ) => {
      await setUpdateAssumptionReq(
        patchConsolidatedAssumption(data, workspaceId, consolidationId, forecastId, assumptionId)
      );
      await loadForecastReport();
    },
    [setUpdateAssumptionReq, loadForecastReport]
  );

  const deleteAssumption = useCallback(
    async (workspaceId: string, consolidationId: string, forecastId: string, assumptionId: string) => {
      await setDeleteAssumptionReq(
        deleteConsolidatedAssumption(workspaceId, consolidationId, forecastId, assumptionId)
      );
      await loadForecastReport();
    },
    [setDeleteAssumptionReq, loadForecastReport]
  );

  const bulkUpdateAssumption = useCallback(
    async (
      data: Partial<BulkUpdatedPredictionsPayload>,
      workspaceId: string,
      consolidationId: string,
      forecastId: string
    ) => {
      await setBulkUpdateAssumptionReq(bulkUpdateConsolidatedForecast(data, workspaceId, consolidationId, forecastId));
      await Promise.all([loadForecast(), loadForecastReport()]);
    },
    [loadForecast, loadForecastReport, setBulkUpdateAssumptionReq]
  );

  const updateForecast = useCallback(
    async (data: ConsolidatedForecastUpdateData | Partial<Forecast>, shouldRefetchReport = false) => {
      try {
        await setUpdateForecastReq(
          updateConsolidatedForecast(
            workspaceId,
            consolidationId,
            forecastId,
            data as ConsolidatedForecastUpdateData
          )
        );
        clearConsolidatedForecastsQueryCache(consolidationId, workspaceId);
        await Promise.all([loadForecast(), loadForecasts()]);
        if (shouldRefetchReport) {
          await loadForecastReport();
        }
      } catch (e) {
        Logger.error(e as string);
      }
    },
    [consolidationId, forecastId, loadForecast, loadForecastReport, setUpdateForecastReq, workspaceId, loadForecasts]
  );

  const deleteForecast = useCallback(async () => {
    try {
      await setDeleteForecastReq(deleteConsolidatedForecast(workspaceId, consolidationId, forecastId));
      navigate(getConsolidatedForecastingUrl(workspaceId, consolidationId, PREDICT_PRODUCT_SLUG));
    } catch (e) {
      Logger.error(e as string);
    }
  }, [consolidationId, forecastId, setDeleteForecastReq, workspaceId, navigate]);

  const refresh = useCallback(() => {
    loadForecast(true);
    loadForecastReport(undefined, undefined, true);
  }, [loadForecast, loadForecastReport]);

  useEffect(() => {
    loadForecast();
    loadForecastReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastId]);

  const values = useMemo(
    () => ({
      updateForecast,
      deleteForecast,
      fetchForecast: loadForecast,
      fetchForecastReport: loadForecastReport,
      setPeriodType,
      updateAssumption,
      deleteAssumption,
      bulkUpdateAssumption,
      refresh,
      forecast: forecastReq.data,
      consolidatedForecastReport: consolidatedForecastReportInfo.forecastReport || null,
      isFetchingReport: isLoadingForecastReport,
      totalPastDates: getTotalPastDates(
        consolidatedForecastReportInfo.periodType,
        consolidatedForecastReportInfo.forecastReport?.data.columns,
        forecastReq.data
      ),
      forecastGraphDate: getForecastGraphDate(forecastReq.data?.forecast_from_date),
      periodType: consolidatedForecastReportInfo.periodType,
      forecastId,
      totalCashPredictRow,
      isLoading:
        bulkUpdateAssumptionReq.busy ||
        updateAssumptionReq.busy ||
        deleteAssumptionReq.busy ||
        forecastReq.busy ||
        updateForecastReq.busy ||
        deleteForecastReq.busy ||
        isLoadingForecastReport
    }),

    [
      updateForecast,
      deleteForecast,
      loadForecast,
      loadForecastReport,
      setPeriodType,
      updateAssumption,
      deleteAssumption,
      bulkUpdateAssumption,
      refresh,
      forecastReq.data,
      forecastReq.busy,
      consolidatedForecastReportInfo.forecastReport,
      consolidatedForecastReportInfo.periodType,
      isLoadingForecastReport,
      getTotalPastDates,
      getForecastGraphDate,
      forecastId,
      totalCashPredictRow,
      bulkUpdateAssumptionReq.busy,
      updateAssumptionReq.busy,
      deleteAssumptionReq.busy,
      updateForecastReq.busy,
      deleteForecastReq.busy
    ]
  );

  return <context.Provider value={values}>{children}</context.Provider>;
};

export const useConsolidatedForecastContext = () => useContext(context);
